<template>
  <div class="auth-container">
    <div class="background-glow" />
    <div class="visual-side">
      <img
        src="@/assets/images/uploads/abstract-circle.svg"
        alt="Optimization Visual"
        class="login-visual"
      />
    </div>

    <div class="form-content">
      <b-link class="brand-logo">
        <vuexy-logo class="logo" />
      </b-link>
      <span style="margin-bottom: 7rem">
        <p class="title">AI Operating System for</p>
        <div>
          <p class="highlight">Digital Marketing</p>
        </div>
      </span>

      <!-- Login Form -->
      <b-form
        v-if="!showForgotPassword && !showNewPasswordRequired"
        class="form-wrapper"
        @submit.prevent="signIn"
      >
        <b-form-group>
          <label class="input-label">Email</label>
          <div class="custom-input-wrapper">
            <span class="input-icon left">
              <feather-icon icon="LockIcon" />
            </span>
            <b-form-input
              v-model="email"
              placeholder="Enter your email"
              type="email"
              class="custom-input"
            />
          </div>
        </b-form-group>

        <b-form-group>
          <label class="input-label">Password</label>
          <div class="custom-input-wrapper">
            <span class="input-icon left">
              <feather-icon icon="LockIcon" />
            </span>
            <b-form-input
              v-model="password"
              placeholder="Enter your password"
              type="password"
              class="custom-input"
            />
          </div>
        </b-form-group>

        <b-link
          class="forgot-password"
          href="#"
          @click.prevent="showForgotPassword = true"
        >
          Forgot Password?
        </b-link>
        <b-button
          variant="none"
          class="sign-in-button"
          type="submit"
          :disabled="isLoading"
        >
          <b-spinner v-if="isLoading" small class="mr-1"></b-spinner>
          {{ isLoading ? "Signing In..." : "Sign In" }}
        </b-button>
      </b-form>

      <!-- New Password Required Form -->
      <b-form
        v-if="showNewPasswordRequired"
        class="form-wrapper"
        @submit.prevent="completeNewPasswordChallenge"
      >
        <b-card-title class="mb-1 title"> Complete Your Profile </b-card-title>
        <b-card-text class="mb-2 privacy-text">
          Please set a new password and provide the required information
        </b-card-text>

        <b-form-group>
          <label class="input-label">New Password</label>
          <div class="custom-input-wrapper">
            <span class="input-icon left">
              <feather-icon icon="LockIcon" />
            </span>
            <b-form-input
              v-model="newRequiredPassword"
              placeholder="Enter new password"
              type="password"
              class="custom-input"
            />
          </div>
        </b-form-group>

        <b-form-group v-if="requiredAttributes.includes('given_name')">
          <label class="input-label">First Name</label>
          <div class="custom-input-wrapper">
            <span class="input-icon left">
              <feather-icon icon="UserIcon" />
            </span>
            <b-form-input
              v-model="firstName"
              placeholder="Enter your first name"
              class="custom-input"
            />
          </div>
        </b-form-group>

        <b-form-group v-if="requiredAttributes.includes('family_name')">
          <label class="input-label">Family Name</label>
          <div class="custom-input-wrapper">
            <span class="input-icon left">
              <feather-icon icon="UsersIcon" />
            </span>
            <b-form-input
              v-model="familyName"
              placeholder="Enter your family name"
              class="custom-input"
            />
          </div>
        </b-form-group>

        <b-form-group v-if="requiredAttributes.includes('phone_number')">
          <label class="input-label">Phone Number</label>
          <div class="custom-input-wrapper">
            <span class="input-icon left">
              <feather-icon icon="PhoneIcon" />
            </span>
            <b-form-input
              v-model="phoneNumber"
              placeholder="Enter your phone number"
              class="custom-input"
            />
          </div>
        </b-form-group>

        <b-button
          :variant="null"
          class="sign-in-button"
          type="submit"
          :disabled="!isNewPasswordFormValid || isNewPasswordLoading"
        >
          <b-spinner v-if="isNewPasswordLoading" small class="mr-1"></b-spinner>
          {{ isNewPasswordLoading ? "Completing..." : "Complete Setup" }}
        </b-button>
      </b-form>

      <!-- Forgot Password Form - Step 1: Email -->
      <b-form
        v-if="showForgotPassword && !showVerificationStep"
        class="form-wrapper"
        @submit.prevent="requestPasswordReset"
      >
        <b-card-title class="mb-1 title"> Forgot Password? 🔒 </b-card-title>
        <b-card-text class="mb-2 privacy-text">
          Enter your email and we'll send you instructions to reset your
          password
        </b-card-text>

        <b-form-group>
          <label class="input-label">Email</label>
          <div class="custom-input-wrapper">
            <span class="input-icon left">
              <feather-icon icon="MailIcon" />
            </span>
            <b-form-input
              v-model="resetEmail"
              placeholder="Enter your email"
              type="email"
              class="custom-input"
            />
          </div>
        </b-form-group>

        <b-button
          :variant="null"
          class="sign-in-button"
          type="submit"
          :disabled="!resetEmail || isResetLoading"
        >
          <b-spinner v-if="isResetLoading" small class="mr-1"></b-spinner>
          {{ isResetLoading ? "Sending..." : "Send Code" }}
        </b-button>

        <p class="privacy-text mt-2">
          <a href="#" @click.prevent="showForgotPassword = false"
            >Back to Login</a
          >
        </p>
      </b-form>

      <!-- Forgot Password Form - Step 2: Verification Code and New Password -->
      <b-form
        v-if="showForgotPassword && showVerificationStep"
        class="form-wrapper"
        @submit.prevent="resetPassword"
      >
        <b-card-title class="mb-1 title"> Reset Password </b-card-title>
        <b-card-text class="mb-2 privacy-text">
          Enter the verification code sent to your email and your new password
        </b-card-text>

        <b-form-group>
          <label class="input-label">Verification Code</label>
          <div class="custom-input-wrapper">
            <span class="input-icon left">
              <feather-icon icon="LockIcon" />
            </span>
            <b-form-input
              v-model="verificationCode"
              placeholder="Enter verification code"
              class="custom-input"
            />
          </div>
        </b-form-group>

        <b-form-group>
          <label class="input-label">New Password</label>
          <div class="custom-input-wrapper">
            <span class="input-icon left">
              <feather-icon icon="LockIcon" />
            </span>
            <b-form-input
              v-model="newPassword"
              placeholder="Enter new password"
              type="password"
              class="custom-input"
            />
          </div>
        </b-form-group>

        <b-button
          :variant="null"
          class="sign-in-button"
          type="submit"
          :disabled="!verificationCode || !newPassword || isConfirmResetLoading"
        >
          <b-spinner
            v-if="isConfirmResetLoading"
            small
            class="mr-1"
          ></b-spinner>
          {{ isConfirmResetLoading ? "Resetting..." : "Reset Password" }}
        </b-button>

        <p class="privacy-text mt-2">
          <a href="#" @click.prevent="showForgotPassword = false"
            >Back to Login</a
          >
        </p>
      </b-form>

      <p v-if="!showForgotPassword" class="privacy-text">
        By logging in, you accept our <a target="_blank" href="https://www.adin.ai/privacy-policy">Privacy Policy</a>
      </p>
    </div>
  </div>
</template>

<script>
import VuexyLogo from "@core/layouts/components/Logo.vue";
import { Auth } from "aws-amplify";
import { onAuthUIStateChange } from "@aws-amplify/ui-components";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import { getHomeRouteForLoggedInUser } from "@/auth/utils";
import { clearFilterDatabase } from "@/@core/utils/filterDB";

export default {
  components: {
    VuexyLogo,
  },
  data() {
    return {
      email: "",
      password: "",
      user: undefined,
      authState: undefined,
      unsubscribeAuth: undefined,
      // Loading states
      isLoading: false,
      isResetLoading: false,
      isConfirmResetLoading: false,
      isNewPasswordLoading: false,
      // Forgot password related data
      showForgotPassword: false,
      showVerificationStep: false,
      resetEmail: "",
      verificationCode: "",
      newPassword: "",
      // New password required challenge
      showNewPasswordRequired: false,
      newRequiredPassword: "",
      firstName: "",
      familyName: "",
      phoneNumber: "",
      challengeUser: null,
      requiredAttributes: [],
    };
  },
  computed: {
    isNewPasswordFormValid() {
      if (!this.newRequiredPassword) return false;

      // Check each required attribute
      if (this.requiredAttributes.includes("given_name") && !this.firstName)
        return false;
      if (this.requiredAttributes.includes("family_name") && !this.familyName)
        return false;
      if (this.requiredAttributes.includes("phone_number") && !this.phoneNumber)
        return false;

      return true;
    },
  },
  async created() {
    await this.cleanBrowserData();
    this.unsubscribeAuth = onAuthUIStateChange((authState, authData) => {
      const lastState = this.$store.getters["auth/getAuthState"];
      this.$store.commit("auth/updateState", authState);
      this.user = authData;
      if (authState === "signedin" && lastState !== "signedin") {
        this.prepareSignIn(authState, authData);
      }
    });
    clearFilterDatabase();
  },
  beforeDestroy() {
    if (this.unsubscribeAuth) {
      this.unsubscribeAuth();
    }
  },
  methods: {
    async cleanBrowserData() {
      await this.$store.dispatch("resetAllStore");
      localStorage.clear();
      sessionStorage.clear();
      await Auth.signOut();
      const cookies = document.cookie.split(";");
      cookies.forEach((cookie) => {
        const eqPos = cookie.indexOf("=");
        const name = eqPos > -1 ? cookie.substr(0, eqPos) : cookie;
        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
      });
    },
    async signIn() {
      try {
        this.isLoading = true;
        const user = await Auth.signIn(this.email, this.password);
        console.log(user);
        if (user.challengeName === "NEW_PASSWORD_REQUIRED") {
          this.showNewPasswordRequired = true;
          this.challengeUser = user;
          this.requiredAttributes =
            user.challengeParam.requiredAttributes || [];
          console.log("Required attributes:", this.requiredAttributes);

          this.$toast({
            component: ToastificationContent,
            position: "top-right",
            props: {
              title: "Setup Required",
              icon: "InfoIcon",
              variant: "info",
              text: "Please complete your profile setup.",
            },
          });
          this.isLoading = false;
          return;
        }

        this.authState = "signedin";
        this.prepareSignIn("signedin", user);
        // Remove the isLoading = false from finally block since it will be handled in prepareSignIn
        return;
      } catch (error) {
        console.error("Login failed:", error);
        this.$toast({
          component: ToastificationContent,
          position: "top-right",
          props: {
            title: "Login Error",
            icon: "AlertCircleIcon",
            variant: "danger",
            text: error.message || "An error occurred during sign-in.",
          },
        });
        this.isLoading = false;
      }
    },
    prepareSignIn(authState, authData) {
      // Keep loading state active until all API requests are completed
      this.isLoading = true;

      const userDataKey = `${
        authData.userDataKey?.split(".")?.slice(0, -1).join(".") || "default"
      }.`;

      const userData = {
        userName: authData.username,
        fullName:
          authData.attributes?.name ||
          authData.attributes?.given_name ||
          "User",
        role: "admin",
        avatar: require("@/assets/images/avatars/user-avatar-small.png"),
        company: "",
        id: 1,
        ability: [
          {
            action: "manage",
            subject: "all",
          },
        ],
      };

      localStorage.setItem("userData", JSON.stringify(userData));
      localStorage.setItem("authState", authState);
      localStorage.setItem("userDataKey", userDataKey);
      this.$store.commit("auth/updateUserDataKey", userDataKey);
      this.$store.commit(
        "auth/updateIdToken",
        localStorage.getItem(`${userDataKey}idToken`)
      );

      this.$router
        .replace(getHomeRouteForLoggedInUser(userData.role))
        .then(() => {
          this.$toast({
            component: ToastificationContent,
            position: "top-right",
            props: {
              title: `Welcome ${userData.fullName}`,
              icon: "CoffeeIcon",
              variant: "success",
              text: `You have successfully logged in as ${userData.role}. Now you can start to explore!`,
            },
          });
          // Only set isLoading to false after redirect and all API operations complete
          this.isLoading = false;
        });
    },
    // Forgot password methods
    async requestPasswordReset() {
      try {
        this.isResetLoading = true;
        await Auth.forgotPassword(this.resetEmail);
        this.showVerificationStep = true;
        this.$toast({
          component: ToastificationContent,
          position: "top-right",
          props: {
            title: "Code Sent",
            icon: "CheckIcon",
            variant: "success",
            text: "Verification code has been sent to your email.",
          },
        });
      } catch (error) {
        console.error("Password reset request failed:", error);
        this.$toast({
          component: ToastificationContent,
          position: "top-right",
          props: {
            title: "Error",
            icon: "AlertCircleIcon",
            variant: "danger",
            text:
              error.message ||
              "An error occurred while requesting password reset.",
          },
        });
      } finally {
        this.isResetLoading = false;
      }
    },
    async resetPassword() {
      try {
        this.isConfirmResetLoading = true;
        await Auth.forgotPasswordSubmit(
          this.resetEmail,
          this.verificationCode,
          this.newPassword
        );
        this.$toast({
          component: ToastificationContent,
          position: "top-right",
          props: {
            title: "Success",
            icon: "CheckIcon",
            variant: "success",
            text: "Password has been reset successfully.",
          },
        });
        // Reset form and go back to login
        this.showForgotPassword = false;
        this.showVerificationStep = false;
        this.resetEmail = "";
        this.verificationCode = "";
        this.newPassword = "";
      } catch (error) {
        console.error("Password reset failed:", error);
        this.$toast({
          component: ToastificationContent,
          position: "top-right",
          props: {
            title: "Error",
            icon: "AlertCircleIcon",
            variant: "danger",
            text: error.message || "An error occurred during password reset.",
          },
        });
      } finally {
        this.isConfirmResetLoading = false;
      }
    },
    async completeNewPasswordChallenge() {
      try {
        this.isNewPasswordLoading = true;
        if (!this.challengeUser) {
          throw new Error("User session expired. Please log in again.");
        }

        const requiredAttributes = {};

        // Only add attributes that are required
        if (this.requiredAttributes.includes("given_name")) {
          requiredAttributes.given_name = this.firstName;
        }

        if (this.requiredAttributes.includes("family_name")) {
          requiredAttributes.family_name = this.familyName;
        }

        if (this.requiredAttributes.includes("phone_number")) {
          requiredAttributes.phone_number = this.phoneNumber;
        }

        const user = await Auth.completeNewPassword(
          this.challengeUser,
          this.newRequiredPassword,
          requiredAttributes
        );

        this.$toast({
          component: ToastificationContent,
          position: "top-right",
          props: {
            title: "Profile Completed",
            icon: "CheckIcon",
            variant: "success",
            text: "Your profile has been successfully set up.",
          },
        });

        this.showNewPasswordRequired = false;
        this.authState = "signedin";
        this.prepareSignIn("signedin", user);
      } catch (error) {
        console.error("Profile setup failed:", error);
        this.$toast({
          component: ToastificationContent,
          position: "top-right",
          props: {
            title: "Setup Error",
            icon: "AlertCircleIcon",
            variant: "danger",
            text: error.message || "An error occurred during profile setup.",
          },
        });
      } finally {
        this.isNewPasswordLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.auth-container {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  height: 100vh;
  background: linear-gradient(to right, #0a0220, #140c3d);
  overflow: hidden;
  padding-right: 8%;
  position: relative;
}

.background-glow {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100vw;
  height: 100vh;
  background: radial-gradient(
      circle at 30% 40%,
      rgba(0, 194, 255, 0.2),
      transparent 50%
    ),
    linear-gradient(135deg, #0a0220 0%, #140c3d 40%, #3b1b60 70%, #512980 100%);
  background-blend-mode: screen;
  z-index: 0;
  pointer-events: none;
}

.form-content {
  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 100%;
  max-width: 400px;
  color: white;
  margin-right: 50px; /* Dilersen daha fazla/az yapabilirsin */
}

.visual-side {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  /* Görseli biraz sağa kaydır */
  width: 55%;
  padding-left: 50px; /* Bu değeri arttırıp azaltarak oynayabilirsin */
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1;
}

.logo-text {
  font-size: 1.6rem;
  font-weight: 700;
  color: white;
  margin-bottom: 0.8rem;
}

.title {
  font-size: 2.2rem;
  font-weight: 250;
  margin-bottom: 2rem;
}

.highlight {
  font-size: 2.5rem;
  color: #ffffff;
  font-weight: bold;
}

.form-wrapper {
  display: flex;
  flex-direction: column;
}

.custom-input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  margin-bottom: 1.5rem;
}

.custom-input {
  background-color: #150c2b;
  color: #ffffff;
  border: 1px solid rgba(115, 103, 240, 0.3);
  border-radius: 12px;
  padding: 0.75rem 2.5rem;
  width: 100%;
  font-size: 1rem;
  box-shadow: 0 0 16px 4px rgba(145, 119, 255, 1);
  transition: all 0.3s ease;
}

.input-label {
  font-size: 0.9rem;
  color: #dcdcdc;
  margin-bottom: 0.4rem;
  display: inline-block;
}

input.custom-input:-webkit-autofill {
  box-shadow: 0 0 0px 1000px #150041 inset !important;
  -webkit-text-fill-color: #fff !important;
}

.custom-input::placeholder {
  color: #150041;
}

.input-icon {
  position: absolute;
  color: rgba(255, 255, 255, 0.5);
  font-size: 1rem;
}

.input-icon.left {
  left: 1rem;
}

.input-icon.right {
  right: 1rem;
  cursor: pointer;
}

.forgot-password {
  display: block;
  margin-bottom: 1rem;
  color: #a18cd1;
  font-size: 0.85rem;
  text-decoration: underline;
}

.sign-in-button {
  background-color: transparent !important;
  border-color: rgba(255, 255, 255, 0.6) !important;
  background: linear-gradient(90deg, #8f35ff 0%, #8f35ff 52%, #003cbe 100%);
  border: 1px solid rgba(255, 255, 255, 0.6);
  border-radius: 999px;
  width: 100%;
  padding: 0.75rem;
  font-weight: bold;
  font-size: 1rem;
  color: white;
  box-shadow: inset 0 0 4px rgba(255, 255, 255, 0.2),
    0 2px 8px rgba(0, 0, 0, 0.2);
  transition: all 0.3s ease;
}

.privacy-text {
  margin-top: 1.5rem;
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.5);
  text-align: left;
}

.privacy-text a {
  color: #7367f0;
  text-decoration: underline;
}

.logo {
  width: 200px;
  height: auto;
  margin-bottom: 7rem;
}

.login-visual {
  max-width: 90%;
  height: auto;
  opacity: 0.9;
}

/* Mobile Responsive Styles */
@media (max-width: 768px) {
  .auth-container {
    justify-content: center;
    align-items: center;
    padding: 20px;
  }

  .visual-side {
    width: 100%;
    height: 100%;
    padding: 0;
    opacity: 0.3; /* Make the SVG transparent so form is readable */
    z-index: 0; /* Put it behind the form */
  }

  .login-visual {
    max-width: 85%;
    max-height: 85vh;
    object-fit: contain;
  }

  .form-content {
    max-width: 90%;
    margin: 0 auto;
    padding: 30px;
    border-radius: 15px;
    backdrop-filter: blur(4px); /* Blur effect behind the form */
    z-index: 3;
  }

  .logo {
    width: 150px;
    margin-bottom: 2rem;
  }

  .title,
  .highlight {
    font-size: 2rem;
    text-align: center;
  }

  .custom-input {
    font-size: 0.9rem;
  }

  .privacy-text {
    text-align: center;
  }
}

@media (max-width: 480px) {
  .form-content {
    padding: 20px;
  }

  .logo {
    width: 120px;
    margin-bottom: 1.5rem;
  }

  .title,
  .highlight {
    font-size: 1.7rem;
  }

  .custom-input {
    font-size: 0.85rem;
    padding: 0.7rem 2.3rem;
  }

  .custom-input-wrapper {
    margin-bottom: 1.2rem;
  }

  .sign-in-button {
    padding: 0.7rem;
    font-size: 0.9rem;
  }
}
</style>
